
import { GameStats, GameState, Level, LEVELS, calculateWPM } from './definitions';
import { UI } from './ui';
import { ParticleSystem, FloatingGrid, GlowingSphere } from './entities';
import { shuffleArray } from './utils';
import * as THREE from 'three';
import { persistence } from './libs/persistence';

export class Game {
  private scene!: THREE.Scene;
  private camera!: THREE.PerspectiveCamera;
  private renderer!: THREE.WebGLRenderer;
  private particles!: ParticleSystem;
  private grid!: FloatingGrid;
  private sphere!: GlowingSphere;

  private ui: UI;
  private state: GameState;
  private stats: GameStats;
  private currentLevel: Level | null = null;
  private levelWords: string[] = [];
  private currentInput: string = '';
  private timerInterval: ReturnType<typeof setInterval> | null = null;
  private animationId: number = 0;
  private clock: THREE.Clock;

  constructor() {
    this.ui = new UI();
    this.clock = new THREE.Clock();
    
    this.state = {
      currentLevel: 1,
      unlockedLevels: 1,
      currentWordIndex: 0,
      currentCharIndex: 0,
      isPlaying: false,
      isPaused: false,
      startTime: 0,
      levelStartTime: 0
    };

    this.stats = {
      score: 0,
      combo: 0,
      maxCombo: 0,
      accuracy: 100,
      totalChars: 0,
      correctChars: 0,
      wordsCompleted: 0,
      wpm: 0
    };

    this.initThree();
    this.setupCallbacks();
    this.loadProgress();
  }

  private initThree(): void {
    this.scene = new THREE.Scene();
    this.scene.fog = new THREE.FogExp2(0x0a0a1a, 0.02);

    this.camera = new THREE.PerspectiveCamera(
      75,
      window.innerWidth / window.innerHeight,
      0.1,
      1000
    );
    this.camera.position.z = 15;

    this.renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    this.renderer.setSize(window.innerWidth, window.innerHeight);
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    document.getElementById('canvas-container')!.appendChild(this.renderer.domElement);

    const ambientLight = new THREE.AmbientLight(0x404040, 0.5);
    this.scene.add(ambientLight);

    const pointLight1 = new THREE.PointLight(0x00f5d4, 1, 50);
    pointLight1.position.set(10, 10, 10);
    this.scene.add(pointLight1);

    const pointLight2 = new THREE.PointLight(0x7b2cbf, 1, 50);
    pointLight2.position.set(-10, -10, 10);
    this.scene.add(pointLight2);

    this.particles = new ParticleSystem(this.scene, 600);
    this.grid = new FloatingGrid(this.scene);
    this.sphere = new GlowingSphere(this.scene);

    window.addEventListener('resize', () => this.onResize());
  }

  private async loadProgress(): Promise<void> {
    const saved = await persistence.getItem('unlockedLevels');
    if (saved) {
      this.state.unlockedLevels = parseInt(saved, 10);
    }
    this.ui.showScreen('start');
  }

  private async saveProgress(): Promise<void> {
    await persistence.setItem('unlockedLevels', String(this.state.unlockedLevels));
  }

  private setupCallbacks(): void {
    this.ui.onStart(() => {
      this.startLevel(1);
    });

    this.ui.onLevelSelect((level: number) => {
      this.startLevel(level);
    });

    this.ui.onInput((char: string) => {
      if (this.state.isPlaying) {
        this.handleInput(char);
      }
    });

    this.ui.onRestart(() => {
      if (this.currentLevel) {
        this.startLevel(this.currentLevel.id);
      }
    });

    this.ui.onMenu(() => {
      this.endLevel();
      this.ui.showLevelSelect(this.state.unlockedLevels);
    });
  }

  private startLevel(levelId: number): void {
    this.currentLevel = LEVELS.find(l => l.id === levelId) || LEVELS[0];
    this.state.currentLevel = levelId;
    this.state.currentWordIndex = 0;
    this.state.currentCharIndex = 0;
    this.state.isPlaying = true;
    this.state.levelStartTime = Date.now();

    this.levelWords = shuffleArray([...this.currentLevel.words]);
    
    this.stats = {
      score: 0,
      combo: 0,
      maxCombo: 0,
      accuracy: 100,
      totalChars: 0,
      correctChars: 0,
      wordsCompleted: 0,
      wpm: 0
    };

    this.currentInput = '';

    this.ui.showScreen('game');
    this.ui.updateLevel(levelId);
    this.ui.updateStats(this.stats);
    this.ui.displayWord(this.levelWords[0], 0);
    this.ui.updateInputDisplay('');
    this.ui.updateProgress(0);
    this.ui.focusInput();

    if (this.timerInterval) {
      clearInterval(this.timerInterval);
    }
    this.timerInterval = setInterval(() => this.updateTimer(), 100);

    this.animate();
  }

  private handleInput(char: string): void {
    if (!this.currentLevel || this.state.currentWordIndex >= this.levelWords.length) return;

    const currentWord = this.levelWords[this.state.currentWordIndex];
    const expectedChar = currentWord[this.state.currentCharIndex];

    this.stats.totalChars++;

    if (char === expectedChar) {
      this.stats.correctChars++;
      this.stats.combo++;
      this.currentInput += char;

      if (this.stats.combo > this.stats.maxCombo) {
        this.stats.maxCombo = this.stats.combo;
      }

      const baseScore = 10;
      const comboBonus = Math.floor(this.stats.combo / 5) * 5;
      const levelBonus = Math.floor((this.currentLevel.accuracyBonus - 1) * 10);
      const scoreGain = baseScore + comboBonus + levelBonus;
      this.stats.score += scoreGain;

      if (this.stats.combo > 0 && this.stats.combo % 5 === 0) {
        this.ui.showComboPopup(this.stats.combo);
        this.particles.burst();
        this.sphere.pulse();
      }

      this.ui.showScorePopup(scoreGain);

      this.state.currentCharIndex++;

      if (this.state.currentCharIndex >= currentWord.length) {
        this.completeWord();
      } else {
        this.ui.displayWord(currentWord, this.state.currentCharIndex);
      }
    } else {
      this.stats.combo = 0;
      this.ui.showInputError();
    }

    this.stats.accuracy = this.stats.totalChars > 0 
      ? (this.stats.correctChars / this.stats.totalChars) * 100 
      : 100;

    const elapsed = Date.now() - this.state.levelStartTime;
    this.stats.wpm = calculateWPM(this.stats.correctChars, elapsed);

    this.ui.updateStats(this.stats);
    this.ui.updateInputDisplay(this.currentInput);
  }

  private completeWord(): void {
    this.stats.wordsCompleted++;
    this.currentInput = '';
    this.state.currentWordIndex++;
    this.state.currentCharIndex = 0;

    this.ui.showPerfectEffect();
    this.sphere.pulse();
    this.particles.burst();

    const progress = this.state.currentWordIndex / this.levelWords.length;
    this.ui.updateProgress(progress);

    if (this.state.currentWordIndex >= this.levelWords.length) {
      this.endLevel();
    } else {
      this.ui.displayWord(this.levelWords[this.state.currentWordIndex], 0);
      this.ui.updateInputDisplay('');
    }
  }

  private updateTimer(): void {
    if (!this.state.isPlaying || !this.currentLevel) return;

    const elapsed = Date.now() - this.state.levelStartTime;
    this.ui.updateTime(elapsed);

    if (elapsed >= this.currentLevel.timeLimit * 1000) {
      this.endLevel();
    }
  }

  private async endLevel(): Promise<void> {
    this.state.isPlaying = false;
    
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
      this.timerInterval = null;
    }

    cancelAnimationFrame(this.animationId);

    if (!this.currentLevel) return;

    const passed = this.stats.score >= this.currentLevel.requiredScore;

    if (passed && this.state.currentLevel >= this.state.unlockedLevels) {
      this.state.unlockedLevels = Math.min(this.state.unlockedLevels + 1, LEVELS.length);
      await this.saveProgress();
    }

    this.ui.showResults(this.stats, this.state.currentLevel, passed);
  }

  private animate(): void {
    if (!this.state.isPlaying) return;

    this.animationId = requestAnimationFrame(() => this.animate());

    const elapsed = this.clock.getElapsedTime();

    this.particles.update(this.stats.combo);
    this.grid.update(elapsed);
    this.sphere.update(elapsed);

    this.camera.position.x = Math.sin(elapsed * 0.2) * 0.5;
    this.camera.position.y = Math.cos(elapsed * 0.15) * 0.3;

    this.renderer.render(this.scene, this.camera);
  }

  private onResize(): void {
    this.camera.aspect = window.innerWidth / window.innerHeight;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(window.innerWidth, window.innerHeight);
  }
}
